import React, { useState, useEffect } from 'react';
import Button from '../../ui/Button';

// Chave para armazenamento temporário no localStorage
const SERVICES_TEMP_STORAGE_KEY = 'bookly_services_temp';
const SETUP_STORAGE_KEY = 'bookly_setup_data';

const ServicesStep = ({ onNext, onBack, initialData = {}, businessType }) => {
  const [categories, setCategories] = useState(initialData.categories || []);
  const [showForm, setShowForm] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    services: []
  });
  
  const [serviceForm, setServiceForm] = useState({
    name: '',
    duration: 30,
    price: '',
    description: ''
  });
  
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [quickAddServiceCategory, setQuickAddServiceCategory] = useState(null);

  // Função para salvar dados automaticamente
  const saveProgress = (updatedCategories) => {
    try {
      console.log('[DEBUG-SERVICES] Salvando progresso de serviços automaticamente');
      
      // Verificar se os dados são válidos
      if (!Array.isArray(updatedCategories)) {
        console.error('[DEBUG-SERVICES] Tentativa de salvar dados inválidos:', typeof updatedCategories);
        return false;
      }
      
      // Logging detalhado sobre o que estamos salvando
      console.log('[DEBUG-SERVICES] Categorias sendo salvas:', {
        quantidade: updatedCategories.length,
        nomes: updatedCategories.map(cat => cat.name),
        totalServicos: updatedCategories.reduce((total, cat) => total + (cat.services?.length || 0), 0)
      });
      
      // 1. Salvar em armazenamento temporário para recuperação rápida
      localStorage.setItem(SERVICES_TEMP_STORAGE_KEY, JSON.stringify(updatedCategories));
      
      // 2. Tentar atualizar também o armazenamento principal
      const mainStorage = localStorage.getItem(SETUP_STORAGE_KEY);
      if (mainStorage) {
        const parsedData = JSON.parse(mainStorage);
        if (parsedData && parsedData.setupData) {
          // Criar uma cópia atualizada dos dados
          const updatedData = {
            ...parsedData,
            setupData: {
              ...parsedData.setupData,
              categories: updatedCategories
            }
          };
          
          // Verificar se os dados são serializáveis/válidos antes de salvar
          try {
            const jsonString = JSON.stringify(updatedData);
            if (!jsonString) {
              throw new Error('Resultado da serialização é vazio');
            }
            
            // Salvar de volta no storage principal
            localStorage.setItem(SETUP_STORAGE_KEY, JSON.stringify(updatedData));
            console.log('[DEBUG-SERVICES] Dados também salvos no storage principal');
          } catch (serializationError) {
            console.error('[DEBUG-SERVICES] Erro ao serializar dados para o storage principal:', serializationError);
            
            // Tentar salvar apenas as categorias sem os dados principais
            try {
              const categoriesOnly = {
                ...parsedData,
                setupData: {
                  ...parsedData.setupData,
                  categories: updatedCategories.map(cat => ({
                    name: cat.name,
                    description: cat.description,
                    services: cat.services ? cat.services.map(svc => ({
                      name: svc.name,
                      duration: svc.duration,
                      price: svc.price,
                      description: svc.description
                    })) : []
                  }))
                }
              };
              
              localStorage.setItem(SETUP_STORAGE_KEY, JSON.stringify(categoriesOnly));
              console.log('[DEBUG-SERVICES] Dados salvos após limpeza');
            } catch (e) {
              console.error('[DEBUG-SERVICES] Falha mesmo após tentar limpeza de dados:', e);
            }
          }
        }
      }
      
      return true;
    } catch (e) {
      console.error('[DEBUG-SERVICES] Erro ao salvar progresso automático:', e);
      return false;
    }
  };

  // Verificar se há dados temporários para recuperar
  useEffect(() => {
    // Só recuperar dados temporários se não temos categorias definidas
    if (categories.length === 0) {
      try {
        const tempData = localStorage.getItem(SERVICES_TEMP_STORAGE_KEY);
        if (tempData) {
          const parsedCategories = JSON.parse(tempData);
          console.log('[DEBUG-SERVICES] Recuperando categorias do armazenamento temporário:', parsedCategories.length);
          setCategories(parsedCategories);
        }
      } catch (e) {
        console.error('[DEBUG-SERVICES] Erro ao recuperar dados temporários:', e);
      }
    }
  }, [categories.length]);

  // Effect para salvar dados sempre que as categorias forem alteradas
  useEffect(() => {
    // Evitar salvar no carregamento inicial (apenas quando houver mudanças reais)
    if (categories.length > 0) {
      saveProgress(categories);
    }
  }, [categories]);

  // Placeholders personalizados para cada tipo de negócio
  const getServicePlaceholder = () => {
    switch(businessType) {
      case 'salao':
        return "Ex: Corte, Escova, Coloração...";
      case 'estetica':
        return "Ex: Limpeza de pele, Drenagem linfática...";
      case 'barbearia':
        return "Ex: Corte masculino, Barba, Sobrancelha...";
      case 'podologia':
        return "Ex: Tratamento de calos, Onicomicose...";
      case 'esmalteria':
        return "Ex: Manicure, Pedicure, Esmaltação em gel...";
      case 'clinicamedica':
        return "Ex: Consulta, Exame, Retorno...";
      case 'spa':
        return "Ex: Massagem relaxante, Massagem terapêutica...";
      case 'pet':
        return "Ex: Banho, Tosa, Consulta veterinária...";
      case 'tatuagem':
        return "Ex: Tatuagem pequena, Tatuagem média...";
      case 'odontologia':
        return "Ex: Limpeza, Restauração, Clareamento...";
      case 'fitness':
        return "Ex: Avaliação física, Treino personalizado...";
      default:
        return "Ex: Nome do serviço...";
    }
  };

  // Placeholders personalizados para categorias
  const getCategoryPlaceholder = () => {
    switch(businessType) {
      case 'salao':
        return "Ex: Cabelo, Unhas, Estética...";
      case 'estetica':
        return "Ex: Facial, Corporal, Depilação...";
      case 'barbearia':
        return "Ex: Cabelo, Barba, Estética...";
      case 'podologia':
        return "Ex: Tratamentos, Embelezamento...";
      case 'esmalteria':
        return "Ex: Mãos, Pés, Nail Art...";
      case 'clinicamedica':
        return "Ex: Consultas, Exames, Procedimentos...";
      case 'spa':
        return "Ex: Massagens, Terapias, Banhos...";
      case 'pet':
        return "Ex: Higiene, Estética, Veterinária...";
      case 'tatuagem':
        return "Ex: Tatuagens, Piercings, Remoção...";
      case 'odontologia':
        return "Ex: Clínica geral, Ortodontia, Estética...";
      case 'fitness':
        return "Ex: Avaliação, Treinos, Nutrição...";
      default:
        return "Ex: Nome da categoria...";
    }
  };

  // Recomendações baseadas no tipo de negócio
  useEffect(() => {
    if (businessType && categories.length === 0) {
      // Simula recomendações baseadas no tipo de negócio
      let recommendedCategories = [];
      
      switch(businessType) {
        case 'salao':
          recommendedCategories = [
            { name: 'Cabelo', description: 'Serviços de cabelo', services: [
              { name: 'Corte feminino', duration: 60, price: 80, description: 'Corte feminino básico' },
              { name: 'Coloração', duration: 120, price: 150, description: 'Coloração completa' }
            ]},
            { name: 'Unhas', description: 'Serviços de manicure e pedicure', services: [
              { name: 'Manicure', duration: 40, price: 35, description: 'Manicure tradicional' }
            ]}
          ];
          break;
        case 'barbearia':
          recommendedCategories = [
            { name: 'Cabelo', description: 'Serviços de cabelo', services: [
              { name: 'Corte masculino', duration: 30, price: 40, description: 'Corte masculino básico' }
            ]},
            { name: 'Barba', description: 'Serviços de barba', services: [
              { name: 'Barba completa', duration: 30, price: 35, description: 'Barba completa com toalha quente' }
            ]}
          ];
          break;
        case 'estetica':
          recommendedCategories = [
            { name: 'Facial', description: 'Tratamentos faciais', services: [
              { name: 'Limpeza de pele', duration: 60, price: 120, description: 'Limpeza de pele profunda' },
              { name: 'Peeling', duration: 45, price: 150, description: 'Peeling facial rejuvenescedor' }
            ]},
            { name: 'Corporal', description: 'Tratamentos corporais', services: [
              { name: 'Drenagem linfática', duration: 60, price: 130, description: 'Massagem de drenagem linfática' },
              { name: 'Modeladora', duration: 60, price: 140, description: 'Massagem modeladora' }
            ]},
            { name: 'Depilação', description: 'Serviços de depilação', services: [
              { name: 'Pernas completas', duration: 60, price: 80, description: 'Depilação de pernas completas' }
            ]}
          ];
          break;
        case 'podologia':
          recommendedCategories = [
            { name: 'Tratamentos', description: 'Tratamentos podológicos', services: [
              { name: 'Tratamento para calos', duration: 45, price: 90, description: 'Remoção e tratamento de calos' },
              { name: 'Onicomicose', duration: 60, price: 120, description: 'Tratamento para fungos nas unhas' }
            ]},
            { name: 'Embelezamento', description: 'Serviços de embelezamento', services: [
              { name: 'Spa dos pés', duration: 60, price: 95, description: 'Relaxamento e hidratação para os pés' }
            ]}
          ];
          break;
        case 'esmalteria':
          recommendedCategories = [
            { name: 'Mãos', description: 'Serviços para mãos', services: [
              { name: 'Manicure', duration: 45, price: 40, description: 'Manicure tradicional' },
              { name: 'Esmaltação em gel', duration: 60, price: 70, description: 'Aplicação de esmalte em gel' }
            ]},
            { name: 'Pés', description: 'Serviços para pés', services: [
              { name: 'Pedicure', duration: 60, price: 50, description: 'Pedicure tradicional' }
            ]},
            { name: 'Nail Art', description: 'Decoração de unhas', services: [
              { name: 'Decoração simples', duration: 30, price: 30, description: 'Desenhos simples nas unhas' }
            ]}
          ];
          break;
        case 'clinicamedica':
          recommendedCategories = [
            { name: 'Consultas', description: 'Consultas médicas', services: [
              { name: 'Consulta clínico geral', duration: 30, price: 200, description: 'Consulta com clínico geral' },
              { name: 'Retorno', duration: 15, price: 100, description: 'Consulta de retorno' }
            ]},
            { name: 'Exames', description: 'Exames médicos', services: [
              { name: 'Check-up básico', duration: 60, price: 300, description: 'Conjunto de exames básicos' }
            ]},
            { name: 'Procedimentos', description: 'Procedimentos médicos', services: [
              { name: 'Pequenas suturas', duration: 45, price: 250, description: 'Procedimento para pequenas suturas' }
            ]}
          ];
          break;
        case 'spa':
          recommendedCategories = [
            { name: 'Massagens', description: 'Serviços de massagem', services: [
              { name: 'Massagem relaxante', duration: 60, price: 150, description: 'Massagem para relaxamento' },
              { name: 'Massagem com pedras quentes', duration: 90, price: 200, description: 'Massagem terapêutica com pedras quentes' }
            ]},
            { name: 'Terapias', description: 'Terapias de relaxamento', services: [
              { name: 'Aromaterapia', duration: 60, price: 140, description: 'Terapia com óleos essenciais' }
            ]},
            { name: 'Banhos', description: 'Banhos terapêuticos', services: [
              { name: 'Ofurô', duration: 45, price: 120, description: 'Banho de imersão terapêutico' }
            ]}
          ];
          break;
        case 'pet':
          recommendedCategories = [
            { name: 'Higiene', description: 'Serviços de higiene pet', services: [
              { name: 'Banho', duration: 60, price: 70, description: 'Banho para cães de porte médio' },
              { name: 'Banho e tosa higiênica', duration: 90, price: 100, description: 'Banho e tosa das regiões higiênicas' }
            ]},
            { name: 'Estética', description: 'Serviços de estética pet', services: [
              { name: 'Tosa completa', duration: 120, price: 120, description: 'Tosa completa para cães de porte médio' },
              { name: 'Hidratação', duration: 60, price: 80, description: 'Hidratação para pelagens ressecadas' }
            ]},
            { name: 'Veterinária', description: 'Serviços veterinários', services: [
              { name: 'Consulta de rotina', duration: 30, price: 150, description: 'Consulta veterinária de rotina' }
            ]}
          ];
          break;
        case 'tatuagem':
          recommendedCategories = [
            { name: 'Tatuagens', description: 'Serviços de tatuagem', services: [
              { name: 'Tatuagem pequena', duration: 60, price: 200, description: 'Tatuagem de até 5cm' },
              { name: 'Tatuagem média', duration: 120, price: 400, description: 'Tatuagem de 5 a 10cm' }
            ]},
            { name: 'Piercings', description: 'Aplicação de piercings', services: [
              { name: 'Piercing simples', duration: 30, price: 100, description: 'Aplicação de piercing básico' }
            ]},
            { name: 'Remoção', description: 'Remoção de tatuagens', services: [
              { name: 'Sessão de remoção', duration: 45, price: 250, description: 'Sessão de remoção a laser' }
            ]}
          ];
          break;
        case 'odontologia':
          recommendedCategories = [
            { name: 'Clínica geral', description: 'Serviços de clínica geral', services: [
              { name: 'Limpeza', duration: 60, price: 150, description: 'Limpeza dentária profissional' },
              { name: 'Restauração', duration: 60, price: 180, description: 'Restauração de dente' }
            ]},
            { name: 'Estética', description: 'Serviços de estética dental', services: [
              { name: 'Clareamento', duration: 90, price: 500, description: 'Clareamento dental de consultório' }
            ]},
            { name: 'Ortodontia', description: 'Serviços ortodônticos', services: [
              { name: 'Manutenção de aparelho', duration: 30, price: 150, description: 'Manutenção mensal de aparelho' }
            ]}
          ];
          break;
        case 'fitness':
          recommendedCategories = [
            { name: 'Avaliação', description: 'Serviços de avaliação', services: [
              { name: 'Avaliação física', duration: 60, price: 150, description: 'Avaliação física completa' },
              { name: 'Avaliação nutricional', duration: 45, price: 130, description: 'Avaliação nutricional' }
            ]},
            { name: 'Treinos', description: 'Serviços de treino', services: [
              { name: 'Personal trainer', duration: 60, price: 100, description: 'Sessão de treino personalizado' },
              { name: 'Treino funcional', duration: 45, price: 70, description: 'Sessão de treino funcional' }
            ]}
          ];
          break;
        // Outros tipos de negócio poderiam ter suas próprias recomendações
        default:
          recommendedCategories = [
            { name: 'Categoria 1', description: 'Descrição da categoria', services: [] }
          ];
      }
      
      setCategories(recommendedCategories);
    }
  }, [businessType, categories.length]);

  const handleAddCategory = () => {
    setShowForm(true);
    setCurrentCategory(null);
    setCategoryForm({
      name: '',
      description: '',
      services: []
    });
  };

  const handleEditCategory = (index) => {
    setShowForm(true);
    setCurrentCategory(index);
    setCategoryForm(categories[index]);
  };

  const handleDeleteCategory = (index) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      const newCategories = [...categories];
      newCategories.splice(index, 1);
      setCategories(newCategories);
      
      // Salvar imediatamente após exclusão
      saveProgress(newCategories);
    }
  };

  const handleCategorySubmit = (e) => {
    e.preventDefault();
    
    let newCategories;
    
    if (currentCategory !== null) {
      // Editando categoria existente
      newCategories = [...categories];
      newCategories[currentCategory] = categoryForm;
    } else {
      // Adicionando nova categoria
      newCategories = [...categories, categoryForm];
    }
    
    setCategories(newCategories);
    setShowForm(false);
    setCategoryForm({
      name: '',
      description: '',
      services: []
    });
    
    // Salvar imediatamente após adicionar/editar categoria
    saveProgress(newCategories);
  };

  const handleCancelCategoryForm = () => {
    setShowForm(false);
    setCategoryForm({
      name: '',
      description: '',
      services: []
    });
  };

  const handleAddService = () => {
    setServiceForm({
      name: '',
      duration: 30,
      price: '',
      description: ''
    });
    setShowServiceForm(true);
    setEditingServiceIndex(null);
  };

  const handleEditService = (index) => {
    setServiceForm(categoryForm.services[index]);
    setShowServiceForm(true);
    setEditingServiceIndex(index);
  };

  const handleDeleteService = (index) => {
    const newServices = [...categoryForm.services];
    newServices.splice(index, 1);
    setCategoryForm({
      ...categoryForm,
      services: newServices
    });
  };

  const handleServiceSubmit = (e) => {
    e.preventDefault();
    
    const newServices = [...categoryForm.services];
    
    if (editingServiceIndex !== null) {
      // Editando serviço existente
      newServices[editingServiceIndex] = serviceForm;
    } else {
      // Adicionando novo serviço
      newServices.push(serviceForm);
    }
    
    const updatedCategoryForm = {
      ...categoryForm,
      services: newServices
    };
    
    setCategoryForm(updatedCategoryForm);
    
    setShowServiceForm(false);
    setServiceForm({
      name: '',
      duration: 30,
      price: '',
      description: ''
    });
    
    // Se estamos editando uma categoria existente, atualizar imediatamente no estado global
    if (currentCategory !== null) {
      const updatedCategories = [...categories];
      updatedCategories[currentCategory] = updatedCategoryForm;
      setCategories(updatedCategories);
      
      // Salvar imediatamente após adicionar/editar serviço
      saveProgress(updatedCategories);
    }
  };

  const handleCancelServiceForm = () => {
    setShowServiceForm(false);
    setServiceForm({
      name: '',
      duration: 30,
      price: '',
      description: ''
    });
  };

  const handleContinue = () => {
    if (categories.length > 0) {
      // Limpar o armazenamento temporário ao avançar
      localStorage.removeItem(SERVICES_TEMP_STORAGE_KEY);
      onNext({ categories });
    }
  };

  const durationOptions = [
    { value: 15, label: '15 minutos' },
    { value: 30, label: '30 minutos' },
    { value: 45, label: '45 minutos' },
    { value: 60, label: '1 hora' },
    { value: 90, label: '1 hora e 30 minutos' },
    { value: 120, label: '2 horas' },
    { value: 150, label: '2 horas e 30 minutos' },
    { value: 180, label: '3 horas' }
  ];

  const handleQuickAddService = (categoryIndex) => {
    setQuickAddServiceCategory(categoryIndex);
    setServiceForm({
      name: '',
      duration: 30,
      price: '',
      description: ''
    });
  };

  const handleQuickServiceSubmit = (e) => {
    e.preventDefault();
    
    const newCategories = [...categories];
    
    if (editingServiceIndex !== null) {
      // Editar serviço existente
      newCategories[quickAddServiceCategory].services[editingServiceIndex] = serviceForm;
    } else {
      // Adicionar novo serviço
      newCategories[quickAddServiceCategory].services.push(serviceForm);
    }
    
    setCategories(newCategories);
    
    setQuickAddServiceCategory(null);
    setEditingServiceIndex(null);
    setServiceForm({
      name: '',
      duration: 30,
      price: '',
      description: ''
    });
    
    // Salvar imediatamente após adicionar/editar serviço rápido
    saveProgress(newCategories);
  };

  const handleCancelQuickService = () => {
    setQuickAddServiceCategory(null);
    setEditingServiceIndex(null);
    setServiceForm({
      name: '',
      duration: 30,
      price: '',
      description: ''
    });
  };

  return (
    <div className="animate-fadeIn">
      <h2 className="text-2xl font-semibold text-center text-gray-800 mb-3">
        Vamos cadastrar seus serviços
      </h2>
      <p className="text-gray-600 text-center mb-8">
        Organize serviços por categorias para facilitar o agendamento
      </p>
      
      {/* Banner informativo sobre preços por profissional */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex items-start">
        <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-blue-800">
          Estes são os preços padrão dos seus serviços. Após o cadastro, você poderá configurar valores personalizados para cada profissional.
        </p>
      </div>
      
      {/* Listagem de categorias e serviços */}
      {!showForm && quickAddServiceCategory === null ? (
        <div className="mb-8">
          {categories.length > 0 ? (
            <div className="space-y-6">
              {categories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                    <div>
                      <h3 className="font-medium text-gray-800">{category.name}</h3>
                      <p className="text-sm text-gray-500">{category.description}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => handleEditCategory(categoryIndex)}
                        className="p-1 text-blue-600 hover:text-blue-800"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCategory(categoryIndex)}
                        className="p-1 text-red-600 hover:text-red-800"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {category.services.length > 0 ? (
                      <>
                        {category.services.map((service, index) => (
                          <div key={index} className="px-4 py-3 flex justify-between items-center">
                            <div>
                              <h4 className="font-medium text-gray-800">{service.name}</h4>
                              <div className="text-sm text-gray-500 space-x-3">
                                <span>{service.duration} min</span>
                                <span>•</span>
                                <span>R$ {service.price}</span>
                              </div>
                              {service.description && (
                                <p className="text-xs text-gray-500 mt-1">{service.description}</p>
                              )}
                            </div>
                            <div className="flex space-x-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setQuickAddServiceCategory(categoryIndex);
                                  setServiceForm({...service});
                                  setEditingServiceIndex(index);
                                }}
                                className="p-1 text-blue-600 hover:text-blue-800"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm('Tem certeza que deseja excluir este serviço?')) {
                                    const newCategories = [...categories];
                                    newCategories[categoryIndex].services.splice(index, 1);
                                    setCategories(newCategories);
                                  }
                                }}
                                className="p-1 text-red-600 hover:text-red-800"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                        <div className="px-4 py-3 flex justify-center">
                          <button
                            type="button"
                            onClick={() => handleQuickAddService(categoryIndex)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Adicionar serviço
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="px-4 py-6 text-center">
                        <p className="text-sm text-gray-500 mb-3">Nenhum serviço cadastrado nesta categoria</p>
                        <button
                          type="button"
                          onClick={() => handleQuickAddService(categoryIndex)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Adicionar serviço
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              <button
                type="button"
                onClick={handleAddCategory}
                className="mt-6 w-full flex items-center justify-center px-4 py-3 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Adicionar categoria
              </button>
            </div>
          ) : (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma categoria cadastrada</h3>
              <p className="mt-1 text-sm text-gray-500">
                Comece criando uma categoria para seus serviços
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleAddCategory}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Adicionar categoria
                </button>
              </div>
            </div>
          )}
        </div>
      ) : quickAddServiceCategory !== null ? (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-8">
          <h3 className="text-lg font-semibold mb-4">
            {editingServiceIndex !== null 
              ? `Editar serviço em ${categories[quickAddServiceCategory].name}`
              : `Adicionar serviço em ${categories[quickAddServiceCategory].name}`
            }
          </h3>
          
          <form onSubmit={handleQuickServiceSubmit}>
            <div className="space-y-3">
              <div>
                <label htmlFor="serviceName" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do serviço
                </label>
                <input
                  type="text"
                  id="serviceName"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  placeholder={getServicePlaceholder()}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="serviceDuration" className="block text-sm font-medium text-gray-700 mb-1">
                    Duração
                  </label>
                  <select
                    id="serviceDuration"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    value={serviceForm.duration}
                    onChange={(e) => setServiceForm({ ...serviceForm, duration: parseInt(e.target.value) })}
                    required
                  >
                    {durationOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="servicePrice" className="block text-sm font-medium text-gray-700 mb-1">
                    Preço (R$)
                  </label>
                  <input
                    type="number"
                    id="servicePrice"
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    value={serviceForm.price}
                    onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                    placeholder="0,00"
                    inputmode="decimal"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="serviceDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição (opcional)
                </label>
                <input
                  type="text"
                  id="serviceDescription"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  placeholder="Breve descrição do serviço"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancelQuickService}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={!serviceForm.name || !serviceForm.price}
                >
                  Salvar serviço
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-8">
          <h3 className="text-lg font-semibold mb-4">
            {currentCategory !== null ? 'Editar Categoria' : 'Nova Categoria'}
          </h3>
          
          <form onSubmit={handleCategorySubmit}>
            <div className="space-y-4 mb-6">
              <div>
                <label htmlFor="categoryName" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da categoria
                </label>
                <input
                  type="text"
                  id="categoryName"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder={getCategoryPlaceholder()}
                  required
                />
              </div>
              
              <div>
                <label htmlFor="categoryDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição (opcional)
                </label>
                <input
                  type="text"
                  id="categoryDescription"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder="Breve descrição da categoria"
                />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Serviços
                  </label>
                  <button
                    type="button"
                    onClick={handleAddService}
                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Adicionar serviço
                  </button>
                </div>
                
                {showServiceForm ? (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-3">
                    <h4 className="font-medium text-sm mb-3">
                      {editingServiceIndex !== null ? 'Editar Serviço' : 'Novo Serviço'}
                    </h4>
                    
                    <div className="space-y-3">
                      <div>
                        <label htmlFor="serviceName" className="block text-sm font-medium text-gray-700 mb-1">
                          Nome do serviço
                        </label>
                        <input
                          type="text"
                          id="serviceName"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                          value={serviceForm.name}
                          onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                          placeholder={getServicePlaceholder()}
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label htmlFor="serviceDuration" className="block text-sm font-medium text-gray-700 mb-1">
                            Duração
                          </label>
                          <select
                            id="serviceDuration"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                            value={serviceForm.duration}
                            onChange={(e) => setServiceForm({ ...serviceForm, duration: parseInt(e.target.value) })}
                            required
                          >
                            {durationOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label htmlFor="servicePrice" className="block text-sm font-medium text-gray-700 mb-1">
                            Preço (R$)
                          </label>
                          <input
                            type="number"
                            id="servicePrice"
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                            value={serviceForm.price}
                            onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                            placeholder="0,00"
                            inputmode="decimal"
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="serviceDescription" className="block text-sm font-medium text-gray-700 mb-1">
                          Descrição (opcional)
                        </label>
                        <input
                          type="text"
                          id="serviceDescription"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                          value={serviceForm.description}
                          onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                          placeholder="Breve descrição do serviço"
                        />
                      </div>
                      
                      <div className="flex justify-end space-x-2">
                        <button
                          type="button"
                          onClick={handleCancelServiceForm}
                          className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={handleServiceSubmit}
                          className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                        >
                          Salvar serviço
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
                
                {categoryForm.services.length > 0 ? (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nome
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Duração
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Preço
                          </th>
                          <th scope="col" className="relative px-3 py-2">
                            <span className="sr-only">Ações</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {categoryForm.services.map((service, index) => (
                          <tr key={index}>
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                              {service.name}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                              {service.duration} min
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                              R$ {service.price}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium space-x-1">
                              <button
                                type="button"
                                onClick={() => handleEditService(index)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteService(index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                Excluir
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-3 text-center text-sm text-gray-500 border border-gray-200 rounded-lg">
                    Nenhum serviço adicionado ainda
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelCategoryForm}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={!categoryForm.name}
              >
                Salvar categoria
              </button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={onBack}>
            Voltar
          </Button>
          <Button
            type="button"
            onClick={handleContinue}
            disabled={categories.length === 0}
          >
            Continuar
          </Button>
        </div>
      )}
    </div>
  );
};

export default ServicesStep; 